import type { IGetToolsResponse, ITool } from 'features/chat/agent-chat-module/services/gpt-chat.service';

export const CONNECTORS_DICTIONARY_KEY = 'connectors';

/** First page is enough to find the few connectors named in the dictionary. */
export const PROMOTED_CONNECTORS_PAGE_SIZE = 100;

/** Used when the connectors row is missing or not valid JSON. */
export const CONNECTORS_DICTIONARY_FALLBACK =
  '{"Outlook":"True","OneDrive":"True","Notion":"True","HubSpot":"True"}';

// Dictionary keys are product names (`OneDrive`). Provider slugs use underscores (`one_drive`).
const connectorKey = (name: string) => name.trim().toLowerCase().replace(/[\s_-]+/g, '');

const isEnabled = (value: unknown) =>
  value === true || (typeof value === 'string' && value.trim().toLowerCase() === 'true');

/** Enabled names, or null when the value is missing or not an object. */
export const parseEnabledConnectorNames = (value: string | null | undefined): Set<string> | null => {
  if (!value?.trim()) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;

  const names = new Set<string>();
  for (const [name, flag] of Object.entries(parsed)) {
    const key = connectorKey(name);
    if (isEnabled(flag) && key) names.add(key);
  }
  return names;
};

export const resolveEnabledConnectorNames = (value: string | null | undefined) =>
  parseEnabledConnectorNames(value) ??
  parseEnabledConnectorNames(CONNECTORS_DICTIONARY_FALLBACK) ??
  new Set<string>();

export const providers = (tool: ITool) => (tool.providers ?? []).map(connectorKey).filter(Boolean);

/** First tool for each enabled provider. A later tool with only those providers is skipped. */
export const selectPromotedConnectors = (tools: ITool[] | undefined, enabled: Set<string>) => {
  if (!tools?.length || enabled.size === 0) return [];

  const seen = new Set<string>();
  return tools.filter((tool) => {
    const matched = providers(tool).filter((name) => enabled.has(name));
    if (matched.length === 0 || matched.every((name) => seen.has(name))) return false;
    matched.forEach((name) => seen.add(name));
    return true;
  });
};

const matchesSearch = (tool: ITool, query: string) => {
  if (!query) return true;
  const providerText = (tool.providers ?? []).join(' ');
  return `${tool.name} ${providerText}`.toLowerCase().includes(query);
};

/**
 * Apps offered in the add-connector picker.
 * Featured catalog tools come first. A provider already taken there is not listed again from My Tools.
 */
export const promotedConnectorsForPicker = (
  featured: ITool[] | undefined,
  mine: ITool[] | undefined,
  enabled: Set<string>,
) => {
  const featuredIds = new Set((featured ?? []).map((tool) => tool.id));
  const mineOnly = (mine ?? []).filter((tool) => !featuredIds.has(tool.id));
  return selectPromotedConnectors([...(featured ?? []), ...mineOnly], enabled);
};

export type PromotedCatalogRow<T extends { slug: string; name: string }> =
  | { kind: 'open_connector'; tool: ITool }
  | { kind: 'composio'; entry: T };

const catalogKeys = (entry: { slug: string; name: string }) =>
  [connectorKey(entry.slug), connectorKey(entry.name)].filter(Boolean);

/**
 * Enabled open connectors replace the Composio app with the same provider.
 * Search matches the open connector's name and provider — not its description.
 */
export const mergePromotedConnectorsIntoCatalog = <T extends { slug: string; name: string }>(
  toolkits: T[],
  promoted: ITool[],
  search: string,
): Array<PromotedCatalogRow<T>> => {
  const query = search.trim().toLowerCase();
  const shown = promoted.filter((tool) => matchesSearch(tool, query));
  const covered = new Set(shown.flatMap(providers));
  const matching = query
    ? toolkits.filter(
        (entry) =>
          entry.slug.toLowerCase().includes(query) || entry.name.toLowerCase().includes(query),
      )
    : toolkits;
  const kept = matching.filter((entry) => !catalogKeys(entry).some((key) => covered.has(key)));

  return [
    ...shown.map((tool) => ({ kind: 'open_connector' as const, tool })),
    ...kept.map((entry) => ({ kind: 'composio' as const, entry })),
  ];
};

/** Composio tools whose provider is already covered by a promoted open connector. */
export const countReplacedComposioTools = (composioTools: ITool[] | undefined, promoted: ITool[]) => {
  const covered = new Set(promoted.flatMap(providers));
  if (!composioTools || covered.size === 0) return 0;
  return composioTools.filter((tool) => providers(tool).some((name) => covered.has(name))).length;
};

/**
 * Show enabled open connectors in place of the Composio tool with the same provider.
 * They are listed on page 1. Search uses name and provider — the tools API also
 * matches description, so "OneDriv" would otherwise return Microsoft Excel.
 */
export const mergePromotedConnectors = (
  data: IGetToolsResponse,
  promoted: ITool[],
  search: string,
  page: number,
  composioTools?: ITool[],
): IGetToolsResponse => {
  const query = search.trim().toLowerCase();
  const pageTools = data.tools.filter((tool) => matchesSearch(tool, query));
  const shown = promoted.filter((tool) => matchesSearch(tool, query));
  const covered = new Set(shown.flatMap(providers));
  const kept = pageTools.filter((tool) => !providers(tool).some((name) => covered.has(name)));
  const keptIds = new Set(kept.map((tool) => tool.id));
  const extras = page === 1 ? shown.filter((tool) => !keptIds.has(tool.id)) : [];
  const hiddenBySearch = data.tools.length - pageTools.length;
  const replaced = countReplacedComposioTools(composioTools ?? data.tools, shown);

  return {
    ...data,
    tools: [...extras, ...kept],
    total_count: Math.max(0, data.total_count - hiddenBySearch - replaced + shown.length),
  };
};
